import React, { useState, useEffect, useRef, type SyntheticEvent } from "react";
import { useDialog } from "~/contexts/useDialog";
import { useFocusContext } from "~/contexts/FocusContext";
import { Button } from "~/widgets/Button/Button";
import styles from "~/widgets/DialogOverlay/DialogOverlay.module.scss";

/**
 * Helper to render message with simple markdown support
 * Converts **bold** to <strong>bold</strong>
 */
const renderMessage = (message: string): React.ReactNode => {
  const parts = message.split(/(\*\*.*?\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

/**
 * DialogOverlay Component
 *
 * Top-level component that renders dialogs from the dialog context.
 * Should be placed in App.tsx to make dialogs available globally.
 */
const DialogOverlay: React.FC = () => {
  const { dialogState, closeDialog } = useDialog();
  const [inputValue, setInputValue] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { setIsOverlayOpen } = useFocusContext();

  // Notify FocusContext when overlay opens/closes
  useEffect(() => {
    setIsOverlayOpen(!!dialogState);
  }, [dialogState, setIsOverlayOpen]);

  // Manage dialog element lifecycle
  useEffect(() => {
    const dialog = dialogRef.current;

    if (dialogState && dialog && !dialog.open) {
      dialog.showModal();

      // Initialize input value
      if (dialogState.type === "text-input") {
        setInputValue(dialogState.initialValue || "");
      } else {
        setInputValue("");
      }

      // Initialize select value with first non-disabled option
      if (dialogState.type === "select") {
        const firstEnabled = dialogState.options.find(opt => !opt.disabled);
        setSelectedValue(firstEnabled?.id || "");
      }

      // For confirm dialogs, focus the dialog so tab order works naturally
      // For input/select dialogs, let the autoFocus on the input/select handle it
      if (dialogState.type === "confirm") {
        dialog.focus();
      }
    } else if (!dialogState && dialog?.open) {
      dialog.close();
    }

    // Handle ESC key press on the native dialog element
    const handleCancel = (e: Event) => {
      e.preventDefault();
      closeDialog();
    };

    if (dialog) {
      dialog.addEventListener('cancel', handleCancel);
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener('cancel', handleCancel);
      }
    };
  }, [dialogState, closeDialog]);

  if (!dialogState) {
    return null;
  }

  const theme = dialogState.theme || 'neutral';

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (dialogState.type === "confirm") {
      dialogState.onConfirm();
    } else if (dialogState.type === "input") {
      const amount = parseInt(inputValue, 10) || 0;
      if (amount > 0) {
        dialogState.onConfirm(amount);
      }
    } else if (dialogState.type === "text-input") {
      if (inputValue.trim()) {
        dialogState.onConfirm(inputValue);
      }
    } else if (dialogState.type === "select") {
      if (selectedValue) {
        dialogState.onConfirm(selectedValue);
      }
    }
    closeDialog();
  };

  const handleBackdropClick = (e: SyntheticEvent) => {
    if (e.target === dialogRef.current) {
      closeDialog();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      // Check if focus is on a button that is NOT the submit button
      const activeElement = document.activeElement as HTMLElement;
      const isNonSubmitButton =
        activeElement?.tagName === "BUTTON" &&
        (activeElement as HTMLButtonElement).type !== "submit";

      if (!isNonSubmitButton) {
        e.preventDefault();
        const form = dialogRef.current?.querySelector('form');
        if (form) {
          form.requestSubmit();
        }
      }
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div
        className={styles.dialogContent}
        onClick={(e: SyntheticEvent) => {
          e.stopPropagation();
        }}
      >
        <h3 className={styles.dialogTitle}>
          {dialogState.title}
          {dialogState.subtitle && (
            <span className={styles.subtitle}>{dialogState.subtitle}</span>
          )}
        </h3>

        <form onSubmit={handleSubmit}>
          {dialogState.type === "confirm" ? (
            <div className={styles.dialogInputGroup}>
              {dialogState.message && (
                <p className={styles.confirmMessage}>{renderMessage(dialogState.message)}</p>
              )}
            </div>
          ) : dialogState.type === "input" ? (
            <div className={styles.dialogInputGroup}>
              {dialogState.message && (
                <p className={styles.message}>{renderMessage(dialogState.message)}</p>
              )}
              <label htmlFor="dialogInput">{dialogState.inputLabel || "Amount"}</label>
              <input
                autoFocus
                id="dialogInput"
                type="number"
                min="0"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="0"
                className={styles.dialogInput}
              />
            </div>
          ) : dialogState.type === "text-input" ? (
            <div className={styles.dialogInputGroup}>
              {dialogState.message && (
                <p className={styles.message}>{renderMessage(dialogState.message)}</p>
              )}
              <label htmlFor="dialogInput">{dialogState.inputLabel || "Text"}</label>
              <input
                autoFocus
                id="dialogInput"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder=""
                className={styles.dialogInput}
              />
            </div>
          ) : (
            <div className={styles.dialogInputGroup}>
              {dialogState.message && (
                <p className={styles.message}>{renderMessage(dialogState.message)}</p>
              )}
              <label htmlFor="dialogSelect">Select an option</label>
              <select
                autoFocus
                id="dialogSelect"
                value={selectedValue}
                onChange={(e) => setSelectedValue(e.target.value)}
                className={styles.dialogInput}
              >
                {dialogState.options.map((option) => (
                  <option
                    key={option.id}
                    value={option.id}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.dialogActions}>
            <Button
              type="button"
              onClick={closeDialog}
              variant="secondary"
              size="md"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant={
                theme === 'positive' ? 'success' :
                  theme === 'negative' ? 'danger' :
                    'primary'
              }
              size="md"
            >
              {dialogState.confirmLabel || "Confirm"}
            </Button>
          </div>
        </form>
      </div>
    </dialog>
  );
};

export default DialogOverlay;
